import BaseChart from './BaseChart';
import { CHART_ANIMATIONS } from '../config/animations';

/**
 * Composite chart implementation for combining multiple chart types
 */
class CompositeChart extends BaseChart {
  /**
   * Get chart specification for composite chart
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    const commonConfig = this.getCommonConfig();
    const data = this.prepareData();
    
    // Get series configurations
    const series = this.options.series || [];
    
    // Get shared data for all series if available
    const sharedData = this.options.sharedData !== false;
    
    // Get shared fields — auto-detect from data when not provided
    const seriesField = this.options.seriesField;
    const xField = this.options.xField || this._resolveNameField();
    const yField = this.options.yField || this._resolveValueField();
    
    return {
      type: 'common',
      data,
      xField,
      yField,
      seriesField,
      
      // Series definitions
      series: series.map(seriesConfig => {
        const baseConfig = {
          type: seriesConfig.type || 'bar',
          xField: seriesConfig.xField || xField,
          yField: seriesConfig.yField || yField,
          seriesField: seriesConfig.seriesField || seriesField
        };
        
        // If series has specific data, link it to the data id
        if (seriesConfig.dataId) {
          baseConfig.dataId = seriesConfig.dataId;
        }
        
        // Add all series-specific configurations
        return {
          ...baseConfig,
          ...seriesConfig
        };
      }),
      
      // Axes configuration
      axes: this.getAxesConfig(),
      
      // Crosshair for hover interactions
      crosshair: {
        xField: { visible: true },
        yField: { visible: true }
      },
      
      // Animation configuration
      animationAppear: {
        duration: 800,
        easing: 'cubicOut',
        ...this.options.animationAppear
      },
      
      // Common configuration from BaseChart
      ...commonConfig
    };
  }
  
  /**
   * Prepare data for VChart format
   * @returns {Array} Formatted data for VChart
   */
  prepareData() {
    // Get data from options
    const data = this.data;
    
    // If data is an array of arrays (multiple datasets), process each
    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
      return data.map((dataset, index) => ({
        id: this.options.series?.[index]?.dataId || `data_${index}`,
        values: dataset
      }));
    } 
    // If data is a single array
    else if (Array.isArray(data)) {
      return [
        {
          id: 'data',
          values: data
        }
      ];
    } 
    // If data is already in the expected format
    else {
      return data;
    }
  }
  
  /**
   * Get axes configuration
   * @returns {Array} Axes configuration
   */
  getAxesConfig() {
    const xField = this.options.xField;
    const xAxisType = xField ? this._detectXAxisType(xField) : 'linear';

    const axesConfig = [
      {
        orient: 'bottom',
        type: xAxisType,
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.xAxisLabelStyle
          },
          formatter: this.options.xAxisLabelFormatter
        },
        title: {
          visible: !!this.options.xAxisTitle,
          text: this.options.xAxisTitle || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.xAxisTitleStyle
          }
        },
        ...this.options.xAxis
      },
      {
        orient: 'left',
        type: 'linear',
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.yAxisLabelStyle
          },
          formatter: this.options.yAxisLabelFormatter
        },
        title: {
          visible: !!this.options.yAxisTitle,
          text: this.options.yAxisTitle || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.yAxisTitleStyle
          }
        },
        grid: {
          visible: true,
          style: {
            stroke: '#E8E8E8',
            lineWidth: 1,
            lineDash: [4, 4],
            ...this.options.gridStyle
          }
        },
        ...this.options.yAxis
      }
    ];
    
    // Add right y-axis if specified
    if (this.options.yAxis2) {
      axesConfig.push({
        orient: 'right',
        type: 'linear',
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.yAxis2LabelStyle
          },
          formatter: this.options.yAxis2LabelFormatter
        },
        title: {
          visible: !!this.options.yAxis2Title,
          text: this.options.yAxis2Title || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.yAxis2TitleStyle
          }
        },
        ...this.options.yAxis2
      });
    }
    
    return axesConfig;
  }
}

export default CompositeChart; 